import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Info, Phone, ChevronLeft, ChevronRight } from "lucide-react";

import defaultImg from "../Images/plug.png"; 
import img from "../Images/pistonbellow.png";
import img1 from "../Images/needle.png";
import img2 from "../Images/gateglobe.png";
import img3 from "../Images/ball.png";
import img4 from "../Images/bvd.png";
import img5 from "../Images/globespec.png";
import img6 from "../Images/ballspec.png";
import img7 from "../Images/pistonspec.png";
import img8 from "../Images/bellowspec.png";
import img9 from "../Images/blow.png";

const ProductCard = ({ product }) => {
  const [activeTab, setActiveTab] = useState('description');
  const [currentSpecIndex, setCurrentSpecIndex] = useState(0);

  const nextSpec = () => {
    if (Array.isArray(product.specImage)) {
      setCurrentSpecIndex((prev) => 
        prev === product.specImage.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevSpec = () => {
    if (Array.isArray(product.specImage)) {
      setCurrentSpecIndex((prev) => 
        prev === 0 ? product.specImage.length - 1 : prev - 1
      );
    }
  };

  return (
    <article className="bg-white rounded-lg shadow-md hover:shadow-xl transition-all duration-300 p-4 sm:p-6">
      <header className="mb-4">
        <h3 className="text-lg sm:text-xl font-bold mb-2 text-gray-900">{product.name}</h3>
      </header>
      
      <div className="bg-gray-100 h-40 sm:h-48 md:h-56 mb-4 rounded-lg overflow-hidden">
        <img 
          src={product.mainImage}
          alt={`${product.name} - industrial valve for flow control applications`}
          className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
          loading="lazy"
          width="400"
          height="300"
        />
      </div>

      <div className="mb-4">
        <nav className="flex border-b border-gray-200" role="tablist">
          <button
            role="tab"
            aria-selected={activeTab === 'description'}
            aria-controls={`${product.id}-description`}
            className={`flex-1 py-2 px-2 sm:px-4 text-sm sm:text-base transition-colors duration-300 ${
              activeTab === 'description'
                ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('description')}
          >
            Description
          </button>
          <button
            role="tab"
            aria-selected={activeTab === 'specs'}
            aria-controls={`${product.id}-specs`}
            className={`flex-1 py-2 px-2 sm:px-4 text-sm sm:text-base transition-colors duration-300 ${
              activeTab === 'specs'
                ? 'border-b-2 border-blue-500 text-blue-600 font-semibold'
                : 'text-gray-600 hover:text-gray-900'
            }`}
            onClick={() => setActiveTab('specs')}
          >
            Specifications
          </button>
        </nav>

        <div className="mt-4">
          {activeTab === 'description' ? (
            <div id={`${product.id}-description`} role="tabpanel">
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed text-justify">
                {product.description}
              </p>
            </div>
          ) : (
            <div id={`${product.id}-specs`} role="tabpanel">
              <div className="relative bg-gray-100 h-32 sm:h-40 md:h-48 rounded-lg overflow-hidden mb-4">
                <img 
                  src={Array.isArray(product.specImage) ? product.specImage[currentSpecIndex] : product.specImage}
                  alt={`${product.name} technical specifications and dimensions`}
                  className="w-full h-full object-contain"
                  loading="lazy"
                  width="400"
                  height="300"
                />
                {Array.isArray(product.specImage) && product.specImage.length > 1 && (
                  <>
                    <button
                      onClick={prevSpec}
                      className="absolute left-1 sm:left-2 top-1/2 -translate-y-1/2 bg-white/80 p-1 sm:p-2 rounded-full hover:bg-white transition-colors"
                      aria-label="Previous specification image"
                    >
                      <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                    </button>
                    <button
                      onClick={nextSpec}
                      className="absolute right-1 sm:right-2 top-1/2 -translate-y-1/2 bg-white/80 p-1 sm:p-2 rounded-full hover:bg-white transition-colors"
                      aria-label="Next specification image"
                    >
                      <ChevronRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-700" />
                    </button>
                    <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 sm:gap-2">
                      {product.specImage.map((_, index) => (
                        <button
                          key={index}
                          className={`w-2 h-2 rounded-full transition-colors ${
                            index === currentSpecIndex ? 'bg-blue-500' : 'bg-gray-300'
                          }`}
                          onClick={() => setCurrentSpecIndex(index)}
                          aria-label={`View specification image ${index + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </div>
              <ul className="space-y-2">
                {product.specifications.map((spec, index) => (
                  <li key={index} className="flex items-start">
                    <Info className="h-4 w-4 sm:h-5 sm:w-5 text-blue-500 mr-2 mt-0.5 flex-shrink-0" aria-hidden="true" />
                    <span className="text-xs sm:text-sm text-gray-600">{spec}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </article>
  );
};

const ManualValves = () => {
  const handleCall = () => {
    window.location.href = 'tel:+9779802781400';
  };

  const products = [
    {
      id: "gate-globe",
      name: "Gate & Globe Valves",
      mainImage: img2,
      specImage: img5,
      description: `Gate valves and globe valves are linear motion flow control valves essential for industrial applications. Gate valves feature a sliding gate-like disc mechanism providing full flow capacity when fully open, making them ideal for on-off service where minimal pressure drop is required. Globe valves offer excellent throttling capabilities with precise flow control, suitable for applications requiring frequent operation and accurate flow regulation in steam, water, and oil systems.`,
      specifications: [
        "Full port design for maximum flow capacity",
        "Pressure classes from 150# to 2500#",
        "Temperature range: -29°C to 593°C",
        "Multiple trim material options",
        "API 600/603 compliance available"
      ]
    },
    {
      id: "ball",
      name: "Ball Valves",
      mainImage: img3,
      specImage: img6,
      description: `Ball valves are essential components used across various industries for controlling the flow of liquids or gases. These valves feature a spherical closure mechanism that allows for quick and efficient shut-off or regulation of fluid flow. With quarter-turn operation, ball valves provide rapid control and reliable sealing, making them ideal for applications requiring frequent operation, tight shut-off, and minimal pressure drop in industrial piping systems.`,
      specifications: [
        "Quarter-turn operation for rapid control",
        "Full and reduced bore options",
        "Pressure ratings up to Class 2500",
        "Fire-safe designs available",
        "Anti-static features standard"
      ]
    },
    {
      id: "piston-bellow",
      name: "Piston & Bellow Sealed Valves",
      mainImage: img,
      specImage: [img7, img8],
      description: `Piston valves and bellow sealed valves are specialized industrial flow control valves designed for critical applications requiring zero external leakage. These valves control the flow of liquids, gases, or steam in pipelines with exceptional reliability. Bellow sealed designs eliminate stem leakage entirely, making them perfect for hazardous, toxic, or expensive media. Piston valves provide precise control in high-pressure applications with extended service life.`,
      specifications: [
        "Zero external leakage with bellow seal design",
        "Pressure ratings up to Class 900",
        "Temperature range: -196°C to 400°C",
        "Available in various material combinations",
        "Compliant with international standards"
      ]
    },
    {
      id: "plug",
      name: "Plug Valves",
      mainImage: defaultImg,
      specImage: "/ProdSpec/PV_Spec.jpg",
      description: `Plug valves feature a unique design consisting of a valve body, cylindrical or tapered plug, and stem, providing efficient sealing and reliable performance. The plug rotates inside the valve body to control fluid flow with quarter-turn operation, offering quick and effortless control. These valves are ideal for on-off service and directional flow control in applications requiring minimal resistance to flow and tight shut-off in chemical processing and petroleum industries.`,
      specifications: [
        "Quarter-turn operation for quick control",
        "Efficient sealing with cylindrical or tapered plug",
        "Available sizes: DN15 to DN300",
        "Material options: stainless steel, cast iron",
        "Pressure ratings up to Class 600"
      ]
    },
    {
      id: "needle",
      name: "Needle Valves",
      mainImage: img1,
      specImage: "/ProdSpec/NV_Spec.jpg",
      description: `Needle valves are precision flow control valves used across various industries for accurate flow regulation. These valves feature a slender, tapered needle-like stem that allows for precise and gradual adjustments to regulate the flow rate of liquids or gases. The fine threading on the stem provides exceptional control accuracy, making needle valves essential for instrumentation, sampling systems, and applications requiring precise flow metering and control.`,
      specifications: [
        "Precise flow control with tapered needle design",
        "Working pressure up to 6000 PSI",
        "Fine adjustment capability",
        "Multiple packing material options",
        "Various end connection configurations"
      ]
    },
    {
      id: "blow-down",
      name: "Blow Down Valves",
      mainImage: img4,
      specImage: img9,
      description: `Blow-down valves are critical safety components incorporated in steam systems and boilers. Their primary function is to maintain optimal operational conditions by removing solid contaminants, sediments, and concentrated dissolved solids from the system. These valves prevent scale buildup, corrosion, and maintain boiler efficiency by facilitating regular blowdown procedures. Quick-opening mechanisms and robust construction ensure reliable operation in demanding boiler applications.`,
      specifications: [
        "Operating pressure up to 32 bar",
        "Temperature capability up to 240°C",
        "Quick-opening mechanism",
        "Robust body construction",
        "Multiple connection options"
      ]
    }
  ];

  // Structured data
  const productListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Industrial Manual Valves",
    "description": "Comprehensive range of manual valves including gate, globe, ball, needle, plug, and blow-down valves for industrial applications",
    "numberOfItems": products.length,
    "itemListElement": products.map((product, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": product.name,
        "description": product.description,
        "image": product.mainImage,
        "offers": {
          "@type": "Offer",
          "availability": "https://schema.org/InStock",
          "price": "Contact for pricing",
          "priceCurrency": "USD"
        }
      }
    }))
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.gorthermenergy.com/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Products",
        "item": "https://www.gorthermenergy.com/products"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": "Manual Valves",
        "item": "https://www.gorthermenergy.com/products/manual-valves"
      }
    ]
  };

  return (
    <>
      <Helmet>
        {/* Optimized title (under 60 characters) */}
        <title>Industrial Manual Valves | Gate Ball & Globe Valves Nepal</title>
        
        {/* Optimized meta description (120-160 characters) */}
        <meta name="description" content="Premium industrial manual valves including gate, ball, globe, needle & plug valves. Expert installation and service in Nepal." />
        
        {/* Keywords */}
        <meta name="keywords" content="manual valves, gate valves, ball valves, globe valves, needle valves, plug valves, industrial valves, flow control, Nepal" />
        
        {/* Canonical URL */}
        <link rel="canonical" href="https://www.gorthermenergy.com/products/manual-valves" />
        
        {/* Language */}
        <html lang="en" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Industrial Manual Valves | Gortherm Energy" />
        <meta property="og:description" content="Comprehensive range of industrial manual valves for flow control applications in Nepal." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/products/manual-valves" />
        <meta property="og:image" content={img2} />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Industrial Manual Valves" />
        <meta name="twitter:description" content="Gate, ball, globe, and specialty valves for industrial applications" />
        <meta name="twitter:image" content={img2} />
        
        {/* Robots */}
        <meta name="robots" content="index, follow, max-image-preview:large" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(productListSchema, null, 2)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema, null, 2)}
        </script>
      </Helmet>

      <div className="pt-16 sm:pt-20 min-h-screen bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
          
          {/* Hero Section */}
          <header className="text-center mb-12 sm:mb-16">
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Industrial Manual Valves
            </h1>
            <h2 className="text-xl sm:text-2xl text-gray-700 mb-4">
              Precision Flow Control Solutions
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
              Discover our comprehensive range of industrial manual valves and flow control solutions, engineered to meet the demanding requirements of modern industrial processes across manufacturing, oil and gas, chemical processing, and power generation industries in Nepal.
            </p>
          </header>

          {/* Product Grid */}
          <section aria-label="Manual valves product catalog">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
              {products.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          </section>

          {/* Additional SEO Content */}
          <section className="mt-12 sm:mt-16 bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-6 text-center">
              Why Choose Our Manual Valves?
            </h2>
            
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Quality Manufacturing</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  All valves manufactured to international standards including API, ANSI, and ISO specifications ensuring reliable performance and longevity.
                </p>
              </article>
              
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Wide Application Range</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Suitable for diverse applications including steam, water, oil, gas, and chemical services across various industries and operating conditions.
                </p>
              </article>
              
              <article>
                <h3 className="text-lg font-semibold mb-2 text-gray-900">Expert Support</h3>
                <p className="text-sm sm:text-base text-gray-700">
                  Professional technical assistance for valve selection, sizing, installation, and maintenance ensuring optimal system performance.
                </p>
              </article>
            </div>
          </section>

          {/* CTA Section */}
          <section className="mt-12 sm:mt-16 text-center bg-white rounded-2xl p-6 sm:p-8 md:p-12 shadow-lg">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4">
              Need Expert Guidance?
            </h2>
            <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-2xl mx-auto">
              Our technical experts are ready to help you select the right valve for your specific application requirements. Get professional consultation today.
            </p>
            <button
              onClick={handleCall}
              className="bg-teal-600 hover:bg-teal-700 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg flex items-center gap-2 mx-auto transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <span className="text-sm sm:text-base font-semibold">Request Consultation</span>
              <Phone className="w-5 h-5" />
            </button>
            
          </section>
        </div>
      </div>
    </>
  );
};

export default ManualValves;